import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "./ui";
import { api, setUserSession } from "../services";
import { saveFlashMessage } from "../utils/flashMessage";
import { useTranslation } from "react-i18next";
import { usePermission } from "../hooks/usePermission";
import { getFirstAccessibleTenantPath } from "../config/routePermissions";
import { loadCachedAccessSummary } from "../services";
import { translateAuthApiMessage } from "../utils/translateAuthApiMessage";
import logo from "../assets/logos/logo.png";
import AuthPageLayout from "./AuthPageLayout";
import ForgotPasswordModal from "./Modals/ForgotPasswordModal";

function LoginPage() {
  const { t } = useTranslation("translation");
  const { refresh } = usePermission();
  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const didRun = useRef(false);

  const handleLogin = async () => {
    setLoading(true);
    setLoginError(null);
    try {
      const trimmed = loginId.trim();
      const payload = { password };
      if (trimmed.includes("@")) {
        payload.email = trimmed.toLowerCase();
      } else {
        payload.username = trimmed;
      }
      const response = await api.post("/login", payload);

      setUserSession(response.data.token, response.data.user, {
        refreshToken: response.data.refreshToken,
        accessTokenExpiresAt: response.data.accessTokenExpiresAt,
        refreshTokenExpiresAt: response.data.refreshTokenExpiresAt,
        user_type: response.data.user_type,
      });
      localStorage.removeItem("adminToken");
      const searchParams = new URLSearchParams(location.search);
      const redirectPath = searchParams.get("redirect");
      window.dispatchEvent(new Event("userLogin"));
      const summary = (await refresh()) || loadCachedAccessSummary();
      if (redirectPath) {
        navigate(redirectPath, { replace: true });
      } else {
        navigate(getFirstAccessibleTenantPath(summary), { replace: true });
      }
    } catch (err) {
      setLoginError(err.response?.data?.message ?? "");
    } finally {
      setLoading(false);
    }
  };

  const isTokenExpired = (token) => {
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp && payload.exp < currentTime;
    } catch {
      return true;
    }
  };

  useEffect(() => {
    if (didRun.current) return;
    didRun.current = true;

    const token = localStorage.getItem("token");
    const adminToken = localStorage.getItem("adminToken");

    if (adminToken && !isTokenExpired(adminToken)) {
      saveFlashMessage(`${t("You are already logged in.")}`, "info");
      navigate("/admin/user-list", { replace: true });
    } else if (token && !isTokenExpired(token)) {
      saveFlashMessage(`${t("You are already logged in.")}`, "info");
      (async () => {
        const summary = (await refresh()) || loadCachedAccessSummary();
        navigate(getFirstAccessibleTenantPath(summary), { replace: true });
      })();
    }
  }, [navigate, refresh, t]);

  const error =
    loginError == null
      ? ""
      : translateAuthApiMessage(loginError, t) || t("An error occurred during login.");

  return (
    <AuthPageLayout>
      <div className="auth-page-card__logo">
        <img src={logo} alt="Loops" />
      </div>

      <form
        className="auth-form"
        onSubmit={(e) => {
          e.preventDefault();
          handleLogin();
        }}
        noValidate
      >
        <div className="auth-field">
          <input
            id="login-id"
            type="text"
            autoComplete="username"
            placeholder={t("auth.login.emailOrUsername", "Email or username")}
            value={loginId}
            onChange={(e) => setLoginId(e.target.value)}
            required
          />
        </div>
        <div className="auth-field">
          <input
            id="login-password"
            type="password"
            autoComplete="current-password"
            placeholder={t("Password")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>

        {error ? <p className="auth-form-error">{error}</p> : null}

        <Button
          type="submit"
          color="primary"
          size="md"
          className="auth-submit"
          disabled={loading}
        >
          {loading ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              />
              {t("Logging in...")}
            </>
          ) : (
            t("Log In")
          )}
        </Button>
      </form>

      <div className="auth-links-row">
        <button
          type="button"
          className="auth-link-muted"
          onClick={() => setShowForgotPassword(true)}
        >
          {t("Forgot Password")}?
        </button>
        <Link to="/register" className="auth-link-primary">
          {t("Create Account")}
        </Link>
      </div>

      <ForgotPasswordModal
        show={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
      />
    </AuthPageLayout>
  );
}

export default LoginPage;
